import * as PropTypes from 'prop-types';
import React from 'react';

import {isActorCollaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import {DataPolicyCollaborator} from 'auth/PermissionConstants';
import AutoSizer from 'components/AutoSizer';
import Breadcrumbs from 'ui-library/Breadcrumbs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {Header1} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as nyc from 'ui-library/NYC';
import pickDefined from 'util/pickDefined';
import {BasicEvent} from 'util/Types';

import NewWorkflowButton from './components/NewWorkflowButton';
import StageConfigurationTable from './components/StageConfigurationTable';
import {WorkflowTemplatesCrumbLink} from './components/WorkflowCrumbLinks';
import ConfigureStageModal from './configuration/ConfigureStageModal';
import WorkflowTemplateBaseSettingsModal from './modals/WorkflowTemplateBaseSettingsModal';
import WorkflowTemplateSettingsModal from './modals/WorkflowTemplateSettingsModal';
import * as WorkflowTemplateUtil from './util/WorkflowTemplateUtil';
import WorkflowConfigurationVisualization from './visualization/WorkflowConfigurationVisualization';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_FLOW_NODE_FULL_SHAPE,
  StageConfiguration,
  StageFlowNode,
  WORKFLOW_TEMPLATE_FULL_SHAPE,
  WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE,
  WorkflowTemplate,
  WorkflowTemplateSummary,
  WorkflowTemplateVersion,
} from './WorkflowConstants';
import * as WorkflowTemplateActions from './WorkflowTemplateActions';

export interface WorkflowTemplateViewScreenProps {
  workflowTemplate: WorkflowTemplate;
  workflowTemplateVersion: WorkflowTemplateVersion;
  stageConfigurations: Array<StageConfiguration>;
  stageConfigurationIdToStageFlowNodes: {[stageConfigurationId: string]: Array<StageFlowNode>};
}

interface State {
  isConfigureStageModalOpen: boolean;
  isSettingsModalOpen: boolean;
  // If editingStageConfiguration is not null ConfigureStageModal is open for editing the stage
  editingStageConfiguration: StageConfiguration | null;
  isEditingVersionDescription: boolean;
  versionDescription: string | null;
  isCreateWorkflowTemplateModalOpen: boolean;
}

class WorkflowTemplateViewScreen extends React.Component<WorkflowTemplateViewScreenProps, State> {
  static propTypes = {
    workflowTemplate: WORKFLOW_TEMPLATE_FULL_SHAPE.isRequired,
    workflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE.isRequired,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    stageConfigurationIdToStageFlowNodes: PropTypes.objectOf(
      PropTypes.arrayOf(STAGE_FLOW_NODE_FULL_SHAPE.isRequired)
    ).isRequired,
  };

  private _isMounted = false;

  state: State = {
    isConfigureStageModalOpen: false,
    isSettingsModalOpen: false,
    // If editingStageConfiguration is not null ConfigureStageModal is open for editing the stage
    editingStageConfiguration: null,
    isEditingVersionDescription: false,
    versionDescription: this.props.workflowTemplateVersion.description,
    isCreateWorkflowTemplateModalOpen: false,
  };

  componentDidMount(): void {
    this._isMounted = true;
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  _isDraft(): boolean {
    return this.props.workflowTemplateVersion.version == null;
  }

  _isEditable(): boolean {
    return this._isDraft() && this.props.workflowTemplateVersion.permissions.writable;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOpenCreateStageConfigurationModal = () => {
    this.setState({isConfigureStageModalOpen: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOpenEditStageConfigurationModal = (stageConfiguration: StageConfiguration) => {
    this.setState({
      isConfigureStageModalOpen: true,
      editingStageConfiguration: stageConfiguration,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseConfigureStageModal = () => {
    this.setState({isConfigureStageModalOpen: false, editingStageConfiguration: null});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOpenSettingsModal = () => {
    this.setState({isSettingsModalOpen: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseSettingsModal = () => {
    // It is possible that we've navigated away from the page by the time this callback is invoked
    if (this._isMounted) {
      this.setState({isSettingsModalOpen: false});
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handlePublishDraft = () => {
    WorkflowTemplateActions.publishDraft(this.props.workflowTemplateVersion.id);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleEditWorkflowTemplate = () => {
    WorkflowTemplateActions.editWorkflowTemplateVersion(
      this.props.workflowTemplateVersion as WorkflowTemplateVersion & {
        workflowTemplate: WorkflowTemplateSummary;
      }
    );
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleChangeVersionDescription = ({target: {value}}: {target: {value: string}}) => {
    this.setState({versionDescription: value});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleClickEditVersionDescription = (e: BasicEvent) => {
    this.setState({isEditingVersionDescription: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSaveVersionDescription = (e: BasicEvent) => {
    WorkflowTemplateActions.editWorkflowTemplateVersionMetadata(this.props.workflowTemplateVersion.id, {
      description: this.state.versionDescription!,
    });
    this.setState({isEditingVersionDescription: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCancelVersionDescription = (e: BasicEvent) => {
    this.setState({
      isEditingVersionDescription: false,
      versionDescription: this.props.workflowTemplateVersion.description,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOpenCreateWorkflowTemplateModal = () => {
    this.setState({isCreateWorkflowTemplateModalOpen: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseCreateWorkflowTemplateModal = () => {
    // It is possible that we've navigated away from the page by the time this callback is invoked
    if (this._isMounted) {
      this.setState({isCreateWorkflowTemplateModalOpen: false});
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCreateNewWorkflowTemplate = ({
    name,
    owner,
    ownerInfo,
    prefix,
    collaborators,
  }: {
    name: string;
    owner: Owner;
    ownerInfo: OwnerInfo;
    prefix: string;
    collaborators: Array<DataPolicyCollaborator>;
  }) =>
    WorkflowTemplateActions.createWorkflowTemplate({
      name,
      owner,
      ownerInfo,
      collaborators: collaborators.map((collaborator) =>
        isActorCollaborator(collaborator)
          ? pickDefined(collaborator, 'id', 'dataPolicyId')
          : pickDefined(collaborator, 'id', 'memberDataPolicyId', 'adminDataPolicyId')
      ),
      prefix,
      workflowTemplateVersionId: this.props.workflowTemplateVersion.id,
    });

  _renderHeaderButtons(): JSX.Element | null {
    if (!this._isDraft()) {
      return (
        <div className="u-spacing-bottom">
          <NewWorkflowButton
            workflowTemplateId={this.props.workflowTemplate.id}
            workflowTemplateVersionId={this.props.workflowTemplateVersion.id}
          />
        </div>
      );
    } else if (this.props.workflowTemplateVersion.permissions.writable) {
      return (
        <div className="u-spacing-bottom">
          <Button
            className="u-tiny-spacing-left"
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={this._handlePublishDraft}
            size={ButtonSize.SMALL}
            text="Publish"
          />
        </div>
      );
    } else {
      return null;
    }
  }

  _renderVersionHeader(): JSX.Element {
    return (
      <div className="u-lr-split">
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE} scrollable={true}>
              {this.props.workflowTemplate.workflowTemplateVersions.map((templateVersionSummary, i) => (
                <Option
                  key={i}
                  onSelect={() => {
                    WorkflowTemplateActions.selectWorkflowTemplateVersion(templateVersionSummary.id);
                  }}
                >
                  {WorkflowTemplateUtil.getWorkflowTemplateVersionVersionLabel(templateVersionSummary)}
                </Option>
              ))}
            </Menu>
          }
        >
          <Button
            isDropdown={true}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text={WorkflowTemplateUtil.getWorkflowTemplateVersionVersionLabel(
              this.props.workflowTemplateVersion
            )}
          />
        </Dropdown>
        {!this._isDraft() && (
          <div>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              onClick={this._handleEditWorkflowTemplate}
              size={ButtonSize.SMALL}
              text="Edit"
            />
            <Button
              className="u-tiny-spacing-left"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              onClick={this._handleOpenCreateWorkflowTemplateModal}
              size={ButtonSize.SMALL}
              text="Save as..."
            />
          </div>
        )}
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <div>
        <ModalAnchor open={this.state.isConfigureStageModalOpen}>
          <ConfigureStageModal
            workflowTemplateVersionId={this.props.workflowTemplateVersion.id}
            stageConfiguration={this.state.editingStageConfiguration}
            onRequestClose={this._handleCloseConfigureStageModal}
          />
        </ModalAnchor>
        <ModalAnchor open={this.state.isSettingsModalOpen}>
          <WorkflowTemplateSettingsModal
            onRequestClose={this._handleCloseSettingsModal}
            workflowTemplate={this.props.workflowTemplate}
          />
        </ModalAnchor>
        <ModalAnchor open={this.state.isCreateWorkflowTemplateModalOpen}>
          <WorkflowTemplateBaseSettingsModal
            title={`Save ${WorkflowTemplateUtil.getWorkflowTemplateVersionVersionLabel(
              this.props.workflowTemplateVersion
            )} as...`}
            onRequestClose={this._handleCloseCreateWorkflowTemplateModal}
            doSubmit={this._handleCreateNewWorkflowTemplate}
          />
        </ModalAnchor>
        <nyc.Container size="lg">
          <div className="workflows-workflowDetailsHeader">
            <div className="workflows-workflowDetailsHeader-column is-expanded">
              <div className="workflows-workflowDetailsHeader-row1" />
              <Breadcrumbs>
                <WorkflowTemplatesCrumbLink />
              </Breadcrumbs>
              <div className="u-flex-grow">
                <span className="workflows-workflowHeader-header">
                  <Header1>
                    {`${this.props.workflowTemplate.name}${this._isDraft() ? ' (Draft)' : ''}`}
                  </Header1>
                  {this.props.workflowTemplate.permissions.readable && (
                    <SettingsIcon onClick={this._handleOpenSettingsModal} />
                  )}
                </span>
              </div>
              {this._renderHeaderButtons()}
            </div>
            <div className="workflows-workflowDetailsHeader-right">
              <AutoSizer disableHeight={true}>
                {({width}) => (
                  <div className="workflows-workflowDetailsHeader-workflowVisualizationContainer">
                    <WorkflowConfigurationVisualization
                      containerWidth={width}
                      isEditable={this._isEditable()}
                      stageConfigurations={this.props.stageConfigurations}
                      stageConfigurationIdToStageFlowNodes={this.props.stageConfigurationIdToStageFlowNodes}
                    />
                  </div>
                )}
              </AutoSizer>
            </div>
          </div>
          <nyc.Row>
            <nyc.Col className="workflowTemplateViewScreen-row" lg={22}>
              {this._renderVersionHeader()}
            </nyc.Col>
          </nyc.Row>
          <nyc.Row>
            <nyc.Col className="workflowTemplateViewScreen-row" lg={11}>
              <div>
                <span className="legacy-font-proxima-title">Description</span>
                {this._isEditable() &&
                  (this.state.isEditingVersionDescription ? (
                    <span>
                      <BenchlingIcon
                        className="u-tiny-spacing-left faded u-primary-pointer metadata-action-icon--complete"
                        icon="check"
                        onClick={this._handleSaveVersionDescription}
                      />
                      <BenchlingIcon
                        className="u-tiny-spacing-left faded u-danger-pointer"
                        icon="close"
                        onClick={this._handleCancelVersionDescription}
                      />
                    </span>
                  ) : (
                    <BenchlingIcon
                      className="u-tiny-spacing-left faded u-primary-pointer"
                      icon="edit"
                      onClick={this._handleClickEditVersionDescription}
                    />
                  ))}
              </div>
              {this.state.isEditingVersionDescription ? (
                <textarea
                  autoFocus={true}
                  value={this.state.versionDescription || ''}
                  onChange={this._handleChangeVersionDescription}
                  rows={3}
                />
              ) : (
                <div>{this.props.workflowTemplateVersion.description}</div>
              )}
            </nyc.Col>
          </nyc.Row>
          <nyc.Row>
            <nyc.Col className="workflowTemplateViewScreen-row" lg={22}>
              <StageConfigurationTable
                isEditable={this._isEditable()}
                onCreateStageConfiguration={this._handleOpenCreateStageConfigurationModal}
                onEditStageConfiguration={this._handleOpenEditStageConfigurationModal}
                stageConfigurations={this.props.stageConfigurations}
              />
            </nyc.Col>
          </nyc.Row>
        </nyc.Container>
      </div>
    );
  }
}

export default WorkflowTemplateViewScreen;
